import * as React from 'react'
import { useTranslation } from 'react-i18next'
import has from 'lodash/has'

import * as SocketActions from '../../../actions/socket'
import * as StoresActions from '../../../actions/stores'
import * as DeviceActions from '../../../actions/device'
import { colors, Tables } from '../../../constants'
import config from '../../../config'
import { selectStoreWithDevices } from '../../../reducers/stores'
import { EstateUtils } from '../../../utils'
import DeviceChannels from './DeviceChannels'
import DeviceUpdateForm from './Forms/Device/DeviceUpdate'
import DeviceFilterForm from './DeviceFilters'
import { ACL, Routes } from '../../../constants'
import { useAppSelector } from '../../../reducers'
import { useHistory, useLocation, useParams } from 'react-router'
import { useAppDispatch } from '../../../actions/types'
import type Device from '../../../types/DeviceMain'
import type { StoreWithDevices } from '../../../types/StoreMain'
import {
    Centered,
    Column,
    Loader,
    Margin,
    Modal,
    Table,
} from '../../../components'
import Button from '../../../components/Button'
import Padding, { Sizes as PaddingSizes } from '../../../components/Padding'
import {
    BottomBoard,
    DevicesBoard,
    LoadMoreHeader,
    LoadMoreWrapper,
    Screenshot,
    StoreDetails,
} from '../../../components/Screens/Estate/Store/Store.styles'
import useSize from '../../../components/utils/useSize'
import Confirm from '../../../components/Confirm'
import deviceTableDisplay from '../../../components/Screens/Estate/Store/Store.table'
import CustomMessages from '../../../components/Screens/Estate/Store/CustomMessage'
import type { LegacyScreenshot } from '../../../types/Screenshots'
import type { URLParams } from './DeviceFilters/types'
import StoreNotes from './StoreNotes'
import StoreInfo from './StoreInfo'
import { usePageTitle } from '../../../actions/app'

interface StorePageDevice extends Device {
    screenshotSrc: string;
    screenshotHighResSrc: string;
    selected: boolean;
}

interface DisplayStore extends StoreWithDevices {
    devices: StorePageDevice[]
}

function StoreView() {
    const dispatch = useAppDispatch()

    // Component state
    const [isDeviceChannelsModalOpen, setIsDeviceChannelsModalOpen] = React.useState(false)
    const [deviceID, setDeviceID] = React.useState<string>()
    const [confirmOpen, setConfirmOpen] = React.useState(false)
    const [confirmHeader, setConfirmHeader] = React.useState('')
    const [confirmContent, setConfirmContent] = React.useState('')
    const [funcConfirm, setFuncConfirm] = React.useState<(e: unknown) => void>()
    const [isDeviceUpdateOpen, setIsDeviceUpdateOpen] = React.useState(false)
    const [isFiltersOpen, setIsFiltersOpen] = React.useState(false)
    const [screenshotsTimestamp, setScreenshotsTimestamp] = React.useState(new Date().getTime())

    // Global state's selectors
    const filters = useAppSelector((state) => state.stores.filters)
    const hasPermToAddDevice = useAppSelector((state) => EstateUtils.hasPermission(state, { acls: [ACL.DEVICE_ADD] }))
    const hasPermToEditDeviceConfig = useAppSelector((state) => EstateUtils.hasPermission(state, { acls: [ACL.DEVICE_SETTINGS_EDIT] }))
    const hasPermToPushContent = useAppSelector((state) => EstateUtils.hasPermission(state, { acls: [ACL.CONTENT_PUSH] }))
    const hasPermToRebootDevice = useAppSelector((state) => EstateUtils.hasPermission(state, { acls: [ACL.DEVICE_REBOOT] }))
    const screenshots = useAppSelector((state) => state.stores.screenshots) as LegacyScreenshot[]
    const selectedEstate = useAppSelector((state) => state.app.selectedEstate)
    const selectedStore = useAppSelector<StoreWithDevices>(selectStoreWithDevices)

    /**
     * Refs for intervals
     */
    const refreshInterval = React.useRef<NodeJS.Timeout>()
    const screenshotTimeout = React.useRef<NodeJS.Timeout>()
    const devicesConnectionInterval = React.useRef<NodeJS.Timeout>()

    // URL query params
    const { code, estate, search = '' } = useParams<URLParams>()
    const { search: searchURL } = useLocation()
    const history = useHistory()

    // Store data
    const store = React.useMemo((): DisplayStore | undefined => {
        // No store selected, or the fetched data does not match the selected store code
        if (!selectedStore || code !== selectedStore.code) {
            return undefined
        }

        const { devices: storeDevices = [] } = selectedStore

        // Extend information for every device in this store
        const devices = storeDevices
            .map((dev: Device & { selected?: boolean }): StorePageDevice => {
                const { id, selected } = dev

                return {
                    ...dev,
                    // Screenshot URL
                    screenshotSrc:
                        `https://volumenetworkstorage.blob.core.windows.net/screenshots/${id}/screenshot-latest.png?t=${screenshotsTimestamp}`,
                    // High resolution screenshot URL
                    screenshotHighResSrc:
                        `https://volumenetworkstorage.blob.core.windows.net/screenshots/${id}/screenshot-latest-highres.png?t=${screenshotsTimestamp}`,
                    // By default, set device as not selected in bulk selection
                    selected: selected ?? false,
                }
            })
            // Order devices
            .sort((dev1, dev2) => {
                return (dev1?.store_location?.name || '').localeCompare(dev2?.store_location?.name)
            })

        return {
            ...selectedStore,
            devices,
        }
    }, [selectedStore, code, screenshotsTimestamp])

    // Change the title of the page adding the store code and name
    usePageTitle(store ? `${code} ${store.name}` : code)

    /**
     * Function to fetch store data
     * @param storeCode
     */
    const selectStore = React.useCallback((storeCode: string) =>  {

        // Fetch socket status information
        dispatch(SocketActions.fetchConnectedDevicesInStores(storeCode))

        // Stop any interval for updating socket status
        if (devicesConnectionInterval.current) {
            clearInterval(devicesConnectionInterval.current)
        }

        // Periodically update socket status
        devicesConnectionInterval.current = setInterval(() => {
            dispatch(SocketActions.fetchConnectedDevicesInStores(storeCode))
        }, config.screenshots.timeToReload)
    }, [dispatch])

    /**
     * Start interval for updating store data
     */
    const initRefreshInterval = React.useCallback(() => {
        if (refreshInterval.current) {
            clearInterval(refreshInterval.current)
        }
    
        refreshInterval.current = setInterval(
            () => setScreenshotsTimestamp(new Date().getTime()),
            config.screenshots.timeToReload,
        )
    }, [])

    /**
     * Effect for fetching store data and periodically updating the devices screenshots
     */
    React.useEffect(() => {
        selectStore(code)
        initRefreshInterval()

        /**
         * Clear all intervals and timeouts
         */
        return () => {
            // Store data interval
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current)
            }

            // Screenshots timeout
            if (screenshotTimeout.current) {
                clearTimeout(screenshotTimeout.current)
            }
    
            // Socket status interval
            if (devicesConnectionInterval.current) {
                clearInterval(devicesConnectionInterval.current);
            }
        }
    }, [code, filters, initRefreshInterval, search, selectStore])

    React.useEffect(() => {
        if (estate === selectedEstate) {
            return
        }

        const url = Routes.buildRoute(Routes['ESTATE:LIST'], {
            estate,
        })
        history.replace([url, searchURL].join('?'))
    }, [estate, history, searchURL, selectedEstate])

    const initScreenshotTimeout = () => {
        if (screenshotTimeout.current) {
            clearTimeout(screenshotTimeout.current)
        }

        if (refreshInterval.current) {
            clearInterval(refreshInterval.current)
        }

        screenshotTimeout.current = setTimeout(() => {
            setScreenshotsTimestamp(new Date().getTime())
            initRefreshInterval()
        }, config.screenshots.timeToRefreshOnDemand)
    }

    const contentPush = (uid: string) => {
        dispatch(DeviceActions.updateContentViaApi(uid))
    }

    /**
     * Ask a device to take a new screenshot and reload the page to get new screenshot
     */
    const demandScreenshot = (uid: string) => {
        dispatch(DeviceActions.demandScreenshotViaApi(uid))
        initScreenshotTimeout()
    }

    const openStatusPanel = (uid: string) => {
        dispatch(DeviceActions.openStatusPanelViaAPI(uid))
    }

    const clearDeviceCache = (uid: string) => {
        dispatch(DeviceActions.clearDeviceCacheViaApi(uid))
    }

    const clearDeviceContent = (uid: string) => {
        dispatch(DeviceActions.clearDeviceContentViaApi(uid))
    }

    const reboot = (uid: string) => {
        dispatch(DeviceActions.rebootDeviceViaApi(uid))
    }

    const restart = (uid: string) => {
        dispatch(DeviceActions.restartDeviceViaApi(uid))
    }

    const settingsPush = (uid: string) => {
        dispatch(DeviceActions.updateSettingsViaApi(uid))
    }

    const reloadStore = () => {
        selectStore(code)
    }

    const onClickChannels = (deviceID: string) => {
        setIsDeviceChannelsModalOpen(true)
        setDeviceID(deviceID)
    }

    const onCloseScreenshot = (id: string) => {
        dispatch(StoresActions.screenshotRemove(id))
    }

    const onOpenDeviceUpdateForm = (deviceID?: string) => {
        setIsDeviceUpdateOpen(true)
        setDeviceID(deviceID)
    }

    const onCloseDeviceUpdateForm = () => {
        setIsDeviceUpdateOpen(false)
    }

    const onToggleChannelsModal = () => {
        setIsDeviceChannelsModalOpen(!isDeviceChannelsModalOpen)
    }

    const openCloseModal = (func: () => void, header: string, content: string) => {
        if (func && typeof func === 'function') {
            setConfirmOpen(!confirmOpen)
            setFuncConfirm(func)
            setConfirmHeader(header)
            setConfirmContent(content)
        } else {
            setConfirmOpen(!confirmOpen)
        }
    }

    const onClickAccept = (e: unknown) => {
        if (funcConfirm) {
            funcConfirm(e)
        }

        setConfirmOpen(!confirmOpen)
    }

    const underlineSearchedItem = (item: Device, searchedItem: string): boolean => {
        if (searchedItem) {
            const searched = searchedItem.toLowerCase()
            if (has(item, 'id') && item.id.toLowerCase().includes(searched)) {
                return true
            }

            if (has(item, 'store_location') && item.store_location.name.toLowerCase().includes(searched)) {
                return true
            }

            if (has(item, 'mac') && item.mac.toLowerCase().includes(searched)) {
                return true
            }
        }

        return false
    }

    const underlineExpiryDateItem = (item: Device) => {
        if (item && item.expires_at) {
            const { expires_at } = item

            if (new Date(expires_at) < new Date()) {
                return true
            }
        }

        return false
    }

    const openCloseFilters = () => {
        setIsFiltersOpen(!isFiltersOpen)
    }

    /**
     * Old Store component code begins here
     */

    const isConnected = useAppSelector(state => state.socket.isConnected)
    const isConnecting = useAppSelector(state => state.socket.isConnecting)
    const hasPermissionToDeleteDevice = useAppSelector(
        (state) => EstateUtils.hasPermission(state, { acls: [ACL.DEVICE_DELETE] }),
    )
    const canSeeNotes = useAppSelector(
        (state) => EstateUtils.hasPermission(state, { acls: [ACL.STORE_NOTE_VIEW] }),
    )
    const { t } = useTranslation()
    const { isMobile } = useSize()

    const { devices = [] } = store || {}

    const [locationToSearch, setLocationToSearch] = React.useState('')
    const [devicsCount, setDevicesCount] = React.useState(10)

    const devicesToRender = React.useMemo(() => {
        const filterDevices = devices?.filter(
            (device) => {
                const { id, store_location } = device
                const idMatches = !search || id.toLowerCase().includes(search.toLowerCase())
                const locationMatches = store_location.name.toLowerCase().includes(locationToSearch.toLowerCase())

                return idMatches && locationMatches
            }
        )
        return filterDevices?.slice(0, devicsCount) ?? []
    }, [devices, devicsCount, search, locationToSearch])

    let customMessagesHeight: string | undefined = canSeeNotes ? '70%' : '100%'

    if (isMobile) {
        customMessagesHeight = undefined
    }

    const bulkSelectionDevices = (e: React.FormEvent<HTMLInputElement>, id: string) => {
        if (typeof e.stopPropagation === 'function') {
            e.stopPropagation()
        }

        const indexDevice = devices.findIndex((device) => device.id === id)
        if (indexDevice > -1) {
            const { selected } = devices[indexDevice]
            devices[indexDevice].selected = !selected
            dispatch(StoresActions.changeDevicesArray(devices))
        }
    }

    const bulkSelectionAllDevices = () => {
        devices.map((device) => (device.selected = !selectedAll))
        dispatch(StoresActions.changeDevicesArray(devices))
        setSelectedAll(!selectedAll)
    }

    const resetSelection = () => {
        setSelectedAll(!selectedAll)
        const resetedDevices = devices.map((device) => {
            return { ...device, selected: false }
        })
        dispatch(StoresActions.changeDevicesArray(resetedDevices))
    }

    const [selectedAll, setSelectedAll] = React.useState(false)

    const storeDataIsEmpty = !selectedStore

    return (
        <>
            <Confirm isOpen={confirmOpen} header={confirmHeader} content={confirmContent} onClickCancel={openCloseModal} onClickAccept={onClickAccept} />
            <DeviceFilterForm isOpen={isFiltersOpen} onClose={openCloseFilters} />
            <DeviceUpdateForm
                isOpen={isDeviceUpdateOpen}
                onClickCancel={onCloseDeviceUpdateForm}
                updateSettingsSocket={settingsPush}
                deviceUID={deviceID}
                reloadStore={reloadStore}
            />
            <StoreInfo
                locationToSearch={locationToSearch}
                onChangeLocationToSearch={setLocationToSearch}
                openCloseFilters={openCloseFilters}
            />
            <StoreDetails className="store">
                <Column className="stores" width={isMobile ? '100%' : '80%'} notFullHeight={isMobile}>
                    <BottomBoard size={PaddingSizes.sm}>
                        <DevicesBoard>
                            {storeDataIsEmpty && (
                                <Centered horizontal>
                                    <Margin top><Loader /></Margin>
                                </Centered>
                            )}
                            {!storeDataIsEmpty && (
                                <Table
                                    data={devicesToRender}
                                    sortable
                                    display={deviceTableDisplay({
                                        hasPermissionToChangeConfig: hasPermToEditDeviceConfig,
                                        hasPermissionToPushContent: hasPermToPushContent,
                                        hasPermissionToReboot: hasPermToRebootDevice,
                                        onClickChannels,
                                        onOpenDeviceUpdateForm,
                                        openCloseModal,
                                        bulkSelectionDevices,
                                        bulkSelectionAllDevices,
                                        reloadStore,
                                        selectedAll,
                                        socket: {
                                            isConnected,
                                            isConnecting,
                                            rebootDevice: reboot,
                                            updateContent: contentPush,
                                            updateSettings: settingsPush,
                                            demandScreenshot,
                                            openStatusPanel,
                                            clearDeviceCache,
                                            clearDeviceContent,
                                            restartApp: restart,
                                        },
                                        t,
                                    })}
                                    addButton={
                                        hasPermToAddDevice
                                            ? () => onOpenDeviceUpdateForm()
                                            : false
                                    }
                                    keyField="id"
                                    tableKey={Tables.STORE_DEVICES}
                                    title={t('devices')}
                                    valueSearch=""
                                    searchedItem={search}
                                    underlineSearchedItem={underlineSearchedItem}
                                    underlineExpiryDateItem={underlineExpiryDateItem}
                                    noMarginTop
                                    withColSelection
                                    rebootDevice={reboot}
                                    resetSelection={resetSelection}
                                    restartApp={restart}
                                    clearCacheDevice={
                                        hasPermissionToDeleteDevice
                                            ? clearDeviceCache
                                            : undefined
                                    }
                                    clearContentDevice={
                                        hasPermissionToDeleteDevice
                                            ? clearDeviceContent
                                            : undefined
                                    }
                                    style={{ minHeight: '400px' }}
                                />
                            )}
                            <DeviceChannels
                                device={deviceID}
                                isOpen={isDeviceChannelsModalOpen}
                                onClickCancel={onToggleChannelsModal}
                                openCloseConfirm={openCloseModal}
                                onOpenDeviceUpdateForm={onOpenDeviceUpdateForm}
                            />
                            <LoadMoreWrapper>
                                {devicesToRender && (
                                    <LoadMoreHeader className="loaded-header">
                                        You're viewing {devicesToRender?.length} of {devices?.length} devices
                                    </LoadMoreHeader>
                                )}
                                {devicesToRender?.length !== devices?.length && (
                                    <>
                                        <Button
                                            className="load-more-button"
                                            onClick={() => {
                                                setDevicesCount(devicsCount + 10)
                                            }}
                                        >
                                            Load more
                                        </Button>
                                        <Button
                                            className="load-all-button"
                                            onClick={() => {
                                                setDevicesCount(devices.length)
                                            }}
                                        >
                                            Load All
                                        </Button>
                                    </>
                                )}
                            </LoadMoreWrapper>
                        </DevicesBoard>
                    </BottomBoard>
                </Column>
                <Column className="notes" width={isMobile ? '100%' : '20%'} notFullHeight={isMobile}>
                    {canSeeNotes && (
                        <Padding
                            right
                            top
                            color={colors.bluedarkest}
                            height={isMobile ? undefined : '70%'}
                            left={isMobile}
                            bottom={isMobile}
                            size={PaddingSizes.sm}
                        >
                            <StoreNotes />
                        </Padding>
                    )}
                    {!isMobile && (
                        <Padding
                            right
                            top
                            className="custom-messages"
                            color={colors.bluedarkest}
                            height={customMessagesHeight}
                            left={isMobile}
                            size={PaddingSizes.sm}
                        >
                            <CustomMessages storeCode={code} />
                        </Padding>
                    )}
                </Column>
            </StoreDetails>
            {!!screenshots.length && (
                <Modal width="auto" height="50%" isOpen onClose={() => onCloseScreenshot(screenshots[0].id)}>
                    <Screenshot src={`data:image/jpeg;base64,${screenshots[0].screenshot}`} alt="screenshot" />
                </Modal>
            )}
        </>
    )
}

export default StoreView
